import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { whatsappLink } from "@/lib/config";
import { Loader2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SERVICES = [
  "Escova Progressiva",
  "Corte Feminino",
  "Tratamento Capilar",
  "Recuperação de Fios",
  "Queda de Cabelo",
  "Consultoria Personalizada",
];

export default function BookingDialog({ open, onOpenChange, defaultService }) {
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [service, setService] = useState(defaultService || "");
  const [date, setDate] = useState("");
  const [notes, setNotes] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (defaultService) setService(defaultService);
  }, [defaultService]);

  const reset = () => {
    setName("");
    setPhone("");
    setService("");
    setDate("");
    setNotes("");
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !phone.trim() || !service) {
      toast.error("Preencha nome, telefone e serviço.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/appointments`, {
        name,
        phone,
        service,
        preferred_date: date,
        notes,
      });
      toast.success("Agendamento enviado! Te chamamos no WhatsApp 💛");

      const msg = `Olá! Meu nome é ${name}. Acabei de fazer um pedido de agendamento pelo site para *${service}*${date ? ` no dia ${date}` : ""}. Pode confirmar pra mim?`;
      window.open(whatsappLink(msg), "_blank");

      reset();
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.status === 409 ? err.response.data.detail : "Não foi possível enviar. Tente pelo WhatsApp!");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="booking-dialog"
        className="bg-[#0A0A0A] border border-[#D4AF37]/40 text-white max-w-lg rounded-none"
      >
        <DialogHeader>
          <DialogTitle className="font-heading text-3xl font-light">
            Agende seu <span className="gold-gradient-text italic">horário</span>
          </DialogTitle>
          <DialogDescription className="text-white/60">
            Preencha e te chamamos no WhatsApp para confirmar tudo.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-4 mt-2">
          <div>
            <Label htmlFor="b-name" className="text-white/80 text-xs uppercase tracking-wider">
              Nome *
            </Label>
            <Input
              id="b-name"
              data-testid="booking-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Seu nome"
              className="bg-[#141414] border-[#222] text-white focus:border-[#D4AF37] mt-2 rounded-none"
              required
            />
          </div>

          <div>
            <Label htmlFor="b-phone" className="text-white/80 text-xs uppercase tracking-wider">
              WhatsApp *
            </Label>
            <Input
              id="b-phone"
              data-testid="booking-phone"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="(11) 99999-9999"
              className="bg-[#141414] border-[#222] text-white focus:border-[#D4AF37] mt-2 rounded-none"
              required
            />
          </div>

          <div>
            <Label className="text-white/80 text-xs uppercase tracking-wider">Serviço *</Label>
            <Select value={service} onValueChange={setService}>
              <SelectTrigger
                data-testid="booking-service"
                className="bg-[#141414] border-[#222] text-white focus:border-[#D4AF37] mt-2 rounded-none"
              >
                <SelectValue placeholder="Escolha um serviço" />
              </SelectTrigger>
              <SelectContent className="bg-[#0A0A0A] border-[#333] text-white rounded-none">
                {SERVICES.map((s) => (
                  <SelectItem key={s} value={s} className="focus:bg-[#D4AF37]/15 focus:text-[#D4AF37]">
                    {s}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="b-date" className="text-white/80 text-xs uppercase tracking-wider">
              Data preferida (opcional)
            </Label>
            <Input
              id="b-date"
              data-testid="booking-date"
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="bg-[#141414] border-[#222] text-white focus:border-[#D4AF37] mt-2 rounded-none"
            />
          </div>

          <div>
            <Label htmlFor="b-notes" className="text-white/80 text-xs uppercase tracking-wider">
              Observações
            </Label>
            <Textarea
              id="b-notes"
              data-testid="booking-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Alguma informação extra?"
              className="bg-[#141414] border-[#222] text-white focus:border-[#D4AF37] mt-2 rounded-none min-h-[80px]"
            />
          </div>

          <button
            data-testid="booking-submit-btn"
            type="submit"
            disabled={loading}
            className="btn-primary-gold w-full justify-center disabled:opacity-50"
          >
            {loading ? (
              <>
                <Loader2 size={16} className="animate-spin" /> Enviando...
              </>
            ) : (
              "Confirmar agendamento"
            )}
          </button>

          <p className="text-xs text-white/40 text-center">
            Ao confirmar, você será redirecionada para o WhatsApp para fechar os detalhes.
          </p>
        </form>
      </DialogContent>
    </Dialog>
  );
}
